import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Space,
  Popconfirm,
  Switch,
  Typography,
  Alert,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

import {
  createApiKey,
  listApiKeys,
  setApiKeyActive,
  deleteApiKey,
} from "../api/apiKeyApi";
import { fetchAllIdClients } from "../api/internal_tool_endpoints";
import { formatClientName } from "../features/dashai/lib/utils";

const { Text } = Typography;
const { Option } = Select;

// Table chrome matching the client product's User-list table (rounded
// container, #fafafa header, Inter, hairline borders) so the two apps' key
// tables read as one system.
const StyledApiKeyTable = styled(Table)`
  .ant-table {
    font-family: "Inter", sans-serif;
  }
  .ant-table-container {
    border: 0.5px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background: #fafafa;
    color: #27272a;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr > td {
    font-family: "Inter", sans-serif;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.85);
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr:hover > td {
    background: #f8fafc;
  }
  .ant-table-tbody > tr.ant-table-placeholder > td {
    color: #a1a1aa;
  }
  .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent;
  }
`;

// The full valid set — staff may mint the machine-to-machine `pipeline` key
// here (clients cannot). Kept in sync with VALID_ENDPOINT_GROUPS in apps/api.
const ENDPOINT_GROUPS = [
  { value: "stores", label: "Stores (outlet search)" },
  { value: "menu", label: "Menu (outlet menus)" },
  { value: "beverages", label: "Beverages (portfolio)" },
  { value: "analytics", label: "Analytics (pricing, compare)" },
  { value: "meta", label: "Meta (filter options)" },
  { value: "market", label: "Market intelligence (premium)" },
  { value: "reviews", label: "Review insights (premium)" },
  { value: "social", label: "Social signals (premium)" },
  { value: "pipeline", label: "Pipeline (machine-to-machine, staff only)" },
];

const EXPIRY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "Never" },
];

const ApiKeysManagementPage = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState(null); // null = all
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [form] = Form.useForm();

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listApiKeys();
      setKeys(data.keys || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  useEffect(() => {
    fetchAllIdClients()
      .then((res) => setAllClients(res?.id_client || res || []))
      .catch(() => setAllClients([]));
  }, []);

  const handleCreate = async (values) => {
    try {
      const result = await createApiKey(values);
      setNewKeyResult(result);
      setCreateModalOpen(false);
      form.resetFields();
      fetchKeys();
      message.success("API key created");
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleToggleActive = async (keyId, isActive) => {
    try {
      await setApiKeyActive(keyId, isActive);
      fetchKeys();
      message.success(isActive ? "API key enabled" : "API key disabled");
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDelete = async (keyId) => {
    try {
      await deleteApiKey(keyId);
      fetchKeys();
      message.success("API key deleted");
    } catch (err) {
      message.error(err.message);
    }
  };

  // Client filter options derived from the loaded keys (staff see all keys).
  const clientOptions = useMemo(() => {
    const ids = [...new Set(keys.map((k) => k.id_client).filter(Boolean))];
    ids.sort();
    return ids;
  }, [keys]);

  const visibleKeys = clientFilter
    ? keys.filter((k) => k.id_client === clientFilter)
    : keys;

  const columns = [
    { title: "Label", dataIndex: "label", key: "label" },
    {
      title: "Client",
      dataIndex: "id_client",
      key: "id_client",
      render: (v) => formatClientName(v),
    },
    {
      title: "Type",
      dataIndex: "managed_by",
      key: "managed_by",
      render: (v) =>
        v === "customer" ? (
          <Tag color="blue" style={{ borderRadius: 9999 }}>
            Customer
          </Tag>
        ) : (
          <Tag color="purple" style={{ borderRadius: 9999 }}>
            Internal
          </Tag>
        ),
    },
    {
      title: "Scopes",
      dataIndex: "allowed_endpoints",
      key: "allowed_endpoints",
      render: (endpoints) => {
        const list = endpoints || [];
        if (list.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={list.join(", ")}>
            <Tag style={{ borderRadius: 9999, cursor: "default" }}>
              {list.length} {list.length === 1 ? "scope" : "scopes"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "Never"),
    },
    {
      title: "Requests",
      dataIndex: "total_requests",
      key: "total_requests",
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Last Used",
      dataIndex: "last_used_at",
      key: "last_used_at",
      render: (val) => (val ? new Date(val).toLocaleString() : "Never"),
    },
    {
      title: "Key",
      dataIndex: "key_suffix",
      key: "key_suffix",
      render: (suffix) => (
        <Text code style={{ whiteSpace: "nowrap" }}>
          ••••{suffix}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      // A reversible toggle, so no confirm — disabling and re-enabling are both
      // one click. Permanent removal is the separate Delete action.
      render: (isActive, record) => (
        <Tooltip title={isActive ? "Active — click to disable" : "Disabled — click to enable"}>
          <Switch
            size="small"
            checked={isActive}
            onChange={(checked) => handleToggleActive(record.key_id, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Copy key ID">
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              aria-label="Copy key ID"
              onClick={() => {
                navigator.clipboard.writeText(record.key_id);
                message.success("Key ID copied");
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this API key?"
            description="Permanently removes the key. This cannot be undone — to pause a key instead, use the Status toggle."
            onConfirm={() => handleDelete(record.key_id)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete permanently">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                aria-label="Delete API key"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    // Offset the fixed 240px sidebar, matching the other admin pages.
    <div
      className="ml-[240px] w-[calc(100%-240px)] min-h-[100vh]"
      style={{ padding: "1.5rem" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: "Satoshi", fontWeight: 700, fontSize: "1.5rem" }}>
            API Keys
          </div>
          <Select
            allowClear
            showSearch
            style={{ minWidth: 220 }}
            placeholder="All clients"
            value={clientFilter}
            onChange={(v) => setClientFilter(v ?? null)}
            optionFilterProp="children"
          >
            {clientOptions.map((c) => (
              <Option key={c} value={c}>
                {formatClientName(c)}
              </Option>
            ))}
          </Select>
        </div>
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          className="h-[3.25rem] bg-[#0e49b5] text-white p-[8px_30px_8px_22px] rounded-full flex items-center gap-2 font-medium transition-colors text-base"
        >
          <PlusOutlined className="mr-1 text-xl" />
          Create API Key
        </button>
      </div>

      <StyledApiKeyTable
        dataSource={visibleKeys}
        columns={columns}
        rowKey="key_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: loading ? null : "No API keys created yet" }}
      />

      {/* Create Key Modal — staff pick the client and whether the key is
          internal (Dashmote) or customer-owned. */}
      <Modal
        title="Create API Key"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ managed_by: "dashmote", expires_in_days: 30 }}
        >
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Enter a label" }]}
          >
            <Input placeholder="e.g. pipeline status callback" />
          </Form.Item>
          <Form.Item
            name="id_client"
            label="Client"
            rules={[{ required: true, message: "Select a client" }]}
          >
            <Select showSearch placeholder="Select a client" optionFilterProp="children">
              {allClients.map((c) => (
                <Option key={c} value={c}>
                  {formatClientName(c)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="managed_by" label="Type">
            <Select>
              <Option value="dashmote">Internal (Dashmote-managed)</Option>
              <Option value="customer">Customer (workspace-owned)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowed_endpoints"
            label="Allowed Endpoints"
            rules={[{ required: true, message: "Select at least one endpoint" }]}
          >
            <Select mode="multiple" placeholder="Select endpoint groups">
              {ENDPOINT_GROUPS.map((ep) => (
                <Option key={ep.value} value={ep.value}>
                  {ep.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="expires_in_days" label="Expiry">
            <Select>
              {EXPIRY_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* New Key Result Modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined />
            API Key Created
          </Space>
        }
        open={!!newKeyResult}
        onCancel={() => setNewKeyResult(null)}
        footer={[
          <Button key="close" onClick={() => setNewKeyResult(null)}>
            Close
          </Button>,
        ]}
      >
        {newKeyResult && (
          <>
            <Alert
              message="Copy this key now — it will not be shown again."
              type="warning"
              showIcon
              className="mb-4"
            />
            <div className="bg-gray-50 p-3 rounded font-mono text-sm break-all">
              {newKeyResult.key}
            </div>
            <Button
              icon={<CopyOutlined />}
              className="mt-2"
              onClick={() => {
                navigator.clipboard.writeText(newKeyResult.key);
                message.success("Key copied to clipboard");
              }}
            >
              Copy to clipboard
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApiKeysManagementPage;
